import makeStyles from '@mui/styles/makeStyles';
import { ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  activeSectionSelector,
  createSectionFundsSelector,
} from '../../../../../../redux/pages/selectors';
import { RaptorTheme, greys } from '../../../../../../styling/theme';
import { BASE_URL } from '../../../../../../utilities/apiConstants';
import GeneralSelect from '../../../../../selects/GeneralSelect';
import { FunctionalityHandlerProps } from '../../Functionality';
import FunctionalityButton from '../functionalityButton';
import GridItem from '../../../../../layout/GridComponents/GridItem';
import RaptorDatePicker from '../../../../../selects/RaptorDatePicker.component';
import dayjs from 'dayjs';
import { Typography } from '@mui/material';
import { getAllRelevantWeekdays } from '../../../../../../utilities/dateUtilities';

function openInNewTab(url: string) {
  return async () => {
    const win = window.open(url, '_blank');
    if (win) {
      win.focus();
      return true;
    } else {
      return false;
    }
  };
}

const useStyles = makeStyles<RaptorTheme>((theme) => ({
  fdiContainer: {
    display: 'flex',
    position: 'relative',
    top: '0.62rem',
  },
  pickerRoot: {
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    marginRight: '0.75rem',
    height: '3.12rem',
  },
  header: {
    position: 'relative',
    // top: '0.31rem',
  },
}));

function CombinedReports(props: FunctionalityHandlerProps): ReactElement {
  // Classes for fdiContainer
  const classes = useStyles();
  const section = useSelector(activeSectionSelector);
  const fundDetails = useSelector(
    createSectionFundsSelector(section || 'ucits'),
  );
  const fundIds = fundDetails != null ? fundDetails.map(({ id }) => id) : [];
  const fundNames =
    fundDetails != null ? fundDetails.map(({ name }) => name) : [];

  const [currentSelection, setCurrentSelection] = useState('none_selected');
  function updateSelection(selection: string) {
    setCurrentSelection(selection);
  }

  const [weekdays] = useState<string[]>(getAllRelevantWeekdays());
  const [selectedDate, setSelectedDate] = useState(weekdays[0]);
  const [pickerDates, setPickerDates] = useState(weekdays);
  // Create a function for generating the url and calling the report when the button is pressed
  // /generate_adepa_report/<fund_id>/<report_date></report_date>
  function generateReport() {
    const openReport = openInNewTab(
      `${BASE_URL}generate_adepa_report/${currentSelection}/${selectedDate}/${currentSelection}_${selectedDate.replace(
        '-',
        '_',
      )}.pdf`,
    );
    openReport();
  }

  useEffect(() => {
    if (!props.availableDates) return;
    if (!currentSelection) return;
    if (currentSelection === 'none_selected') return;
    if (!Object.keys(props.availableDates).includes(currentSelection)) return;
    setPickerDates(props.availableDates[currentSelection] ?? []);
  }, [currentSelection, props.availableDates]);

  return (
    <GridItem
      card
      xs={props.size}
      cardStyle={{ height: '100%', padding: '1.25rem' }}
    >
      <div className={classes.header}>
        <Typography
          variant="h2"
          style={{
            fontSize: '1.50rem',
            width: '100%',
            fontWeight: 500,
            textAlign: 'left',
          }}
          className={classes.header}
        >
          {props.cardTitle}
        </Typography>
      </div>
      <div className={classes.fdiContainer}>
        <GeneralSelect
          labels={fundNames}
          values={fundIds}
          selected={currentSelection}
          setSelected={updateSelection}
          placeholderValue="None Selected"
          placeholderColor={greys.grey900}
          selectTitle={'Select a fund'}
          customStyles={{
            pickerRoot: classes.pickerRoot,
          }}
        />
        {currentSelection !== 'none_selected' && (
          <>
            <RaptorDatePicker
              title={'Position Date'}
              handler={(val) => {
                setSelectedDate(dayjs(val).format('YYYY-MM-DD'));
              }}
              datesToInclude={pickerDates}
              selectedDate={dayjs(selectedDate)}
              tiedToActiveDate={false}
            />

            <FunctionalityButton
              handler={generateReport}
              title="Run PDF Report"
              disabled={!pickerDates.includes(selectedDate)}
            />
          </>
        )}
      </div>
    </GridItem>
  );
}

export default CombinedReports;
